import {
    BreakpointObserver,
    MediaMatcher,
} from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    Component,
    isDevMode,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    MatSidenav,
    MatSidenavContainer,
} from '@angular/material/sidenav';
import { Router } from '@angular/router';
import {
    AppStateFacade,
    Setting,
    UserAuthState,
} from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
} from '@app/router';
import {
    FOLDER_ID_PARAM,
    IAlert,
    IAuthConfig,
    IHostLicense,
    ILanguageInfo,
    ILicense,
    IUser,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { ExpiredModalComponent } from '@pages/license/modals/expired/expired.modal';
import {
    AlertsService,
    AuthService,
    LanguageService,
    ModalService,
    NotificationSeverity,
    SignalRService,
    StorageService,
} from '@services';
import {
    SnackBarType,
    UiSnackBarService,
} from '@uipath/angular/components/ui-snackbar';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import {
    forkJoin,
    Observable,
    Subject,
} from 'rxjs';
import {
    distinctUntilChanged,
    switchMap,
    take,
    takeUntil,
    throttleTime,
} from 'rxjs/operators';

import { InactivityTimer } from './inactivity-timer';

const BREAKPOINT = '(min-width: 960px)';

const enum MenuItemType {
    page = 'page',
    userProfile = 'userProfile',
    logout = 'logout',
}

interface IUserMenuItem {
    type: MenuItemType;
    page?: PageMetadata;
}

const SEVERITY_SNACKBAR_MAP: Map<NotificationSeverity, SnackBarType> = new Map([
    [NotificationSeverity.Info, SnackBarType.Info],
    [NotificationSeverity.Success, SnackBarType.Success],
    [NotificationSeverity.Warn, SnackBarType.Warning],
    [NotificationSeverity.Error, SnackBarType.Error],
    [NotificationSeverity.Fatal, SnackBarType.Error],
]);

@Component({
    templateUrl: 'uipath.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        MediaMatcher,
        BreakpointObserver,
        InactivityTimer,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiPathComponent implements OnInit, OnDestroy {
    public get sidebarWidth() {
        return this.sidenav.mode === 'side' ?
            this._sidebarWidth :
            0;
    }

    public get helpUrl() {
        return this._appState.settings.get(Setting.helpUrl);
    }

    public get isDevMode(): boolean {
        return isDevMode();
    }
    public authConfig$: Observable<IAuthConfig>;
    public uiPathLogoRoute: any;
    public showAccessDeniedTitle = false;
    public deniedPage?: PageMetadata;
    public sideNavOpened = true;
    public languages$!: Observable<ILanguageInfo[]>;
    public license$: Observable<IHostLicense | ILicense>;
    public menuItems!: IUserMenuItem[];
    public customTitle?: string;
    public isHost!: boolean;
    public currentUser$!: Observable<IUser | null>;
    public hasFolder!: boolean;

    @ViewChild('sidenav', { static: true }) sidenav!: MatSidenav;
    @ViewChild('sidenavContainer', { static: true }) sidenavContainer!: MatSidenavContainer;
    private _sidebarWidth!: number;

    private _defaultTenantName = 'Default';
    private _destroyed$ = new Subject();

    constructor(
        _inactivityTimer: InactivityTimer,
        public translateService: TranslateService,
        private _appState: AppStateFacade,
        private _router: Router,
        private _signalRService: SignalRService,
        private _modalService: ModalService,
        private _auth: AuthService,
        private _breakpointObserver: BreakpointObserver,
        private _storageService: StorageService,
        private _alertsService: AlertsService,
        private _languageService: LanguageService,
        private _snack: UiSnackBarService,
        private _pageRegistry: PageMetadataRegistry,
    ) {
        this.authConfig$ = this._appState.authConfig.value$;

        this._appState.settings.get$(Setting.customTitle)
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe((updatedCustomTitle) => this.customTitle = updatedCustomTitle);

        this.uiPathLogoRoute = '/';


        this._signalRService.license$
            .pipe(
                switchMap(() => forkJoin([
                    this._appState.user.license.fetch(),
                    this._appState.settings.fetch(),
                ])),
                takeUntil(this._destroyed$),
            ).subscribe();

        this._appState.user.folder.value$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(() => this.hasFolder = this._appState.user.folder.hasAny);

        this.license$ = this._appState.user.license.value$ as Observable<IHostLicense | ILicense>;
    }

    ngOnInit() {
        this._preventClickJacking();

        this.currentUser$ = this._appState.user.value$;
        this.isHost = this._appState.user.loggedInAsHost;

        if (!this.isHost) {
            this._appState.user.folder.value$
                .pipe(
                    filterNullOrUndefined(),
                    takeUntil(this._destroyed$),
                )
                .subscribe(folder => {
                    const paramMap = this._router.routerState.snapshot.root.queryParamMap;

                    if (
                        Number(paramMap.get(FOLDER_ID_PARAM)) === folder.id ||
                        !this._appState.user.folder.hasAny
                    ) {
                        return;
                    }

                    this._router.navigate([], {
                        queryParams: {
                            [FOLDER_ID_PARAM]: folder.id,
                        },
                        preserveFragment: true,
                        queryParamsHandling: 'merge',
                    });
                });
        }

        this._storageService
            .watch('loggedInUser')
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(event => {
                if (event.newValue === 'null') {
                    this._modalService.closeAllDialogs();
                    this.logout();
                    this._modalService.onAlert(this.translateService.instant('main_logged_out_message'));
                } else if (event.newValue !== this._appState.user.value!.id.toString()) {
                    // the user has changed, reload the page
                    window.location.reload();
                }
            });

        this._signalRService.connect();

        this._appState.language.change$
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(
                () => this._signalRService.monitoringSubscribe(),
            );

        this._appState.user.license.value$
            .pipe(
                take(1),
                takeUntil(this._destroyed$),
            )
            .subscribe(license => {
                if (
                    !!license &&
                    license.isRegistered &&
                    !license.isExpired &&
                    license.expirationDate < new Date()
                ) {
                    this._modalService.openModal(
                        ExpiredModalComponent,
                        license,
                    );
                }
            });

        this._signalRService.notification$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(this._onNotificationReceived);

        this._signalRService.watchPermissions();
        this._signalRService.watchAllLicenses();
        this.uiPathLogoRoute = this._appState.user.authState === UserAuthState.LoggedInAsTenant ? '/' : '/tenants';


        this.menuItems = [
            {
                type: MenuItemType.userProfile,
            },
            ...this._pageRegistry.userMenuPages.map(p => ({
                page: p,
                type: MenuItemType.page,
            })),
            {
                type: MenuItemType.logout,
            },
        ];
        this.languages$ = this._languageService.getAll();

        if (this._breakpointObserver.isMatched(BREAKPOINT)) {
            this.sidenav.mode = 'side';
        } else {
            this.sideNavOpened = false;
            this.sidenav.mode = 'over';
        }
        this._watchScreen();
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._signalRService.disconnect();
    }

    public getUserName(currentUser: IUser): string {
        const displayName = currentUser.fullName.trim() ? currentUser.fullName : currentUser.userName;

        if (
            currentUser.tenantDisplayName &&
            currentUser.tenantDisplayName !== this._defaultTenantName
        ) {
            return `${currentUser.tenantDisplayName}/${displayName}`;
        }

        return displayName;
    }

    public getInitials(currentUser: IUser): string {
        if (!currentUser.fullName.trim()) { return currentUser.userName[0]; }
        return `${currentUser.name[0] || ''}${currentUser.surname[0] || ''}`;
    }

    public toggleSidebar() {
        this.sidenav.toggle();
    }

    public logout() {
        this._signalRService.disconnect();
        this._auth.logout();
    }

    public changeLanguage(language: ILanguageInfo['Name']) {
        forkJoin([
            this._appState.language
                .set(language),
            this._appState.user
                .saveLanguage(language),
        ]).subscribe();
    }

    public updateSidebarWidth = (width: number) => {
        this._sidebarWidth = width;
        this.sidenavContainer.updateContentMargins();
    }

    public onOpenNotificationSettings() {
        this._router.navigate(['/profile']);
    }

    private _watchScreen(): void {
        this._breakpointObserver.observe(BREAKPOINT)
            .pipe(
                distinctUntilChanged(),
                // cap to 60 events per second
                throttleTime(1000 / 60),
                takeUntil(this._destroyed$),
            )
            .subscribe(state => {
                const matches = state.matches;
                this.sidenav.mode = matches ? 'side' : 'over';
                if (matches) {
                    this.sidenav.open();
                } else if (matches === false) {
                    this.sidenav.close();
                }
            });
    }

    private _preventClickJacking() {
        if (window.top !== window.self) {
            window.top.location.replace(window.self.location.href);
        }
    }

    private _onNotificationReceived = (notification: IAlert) => {
        if (notification.severity === NotificationSeverity.Error ||
            notification.severity === NotificationSeverity.Fatal ||
            (notification.data && notification.data.forceDisplay)) {
            this._showNotification(notification);
            this._alertsService.incrementCount();
        }
    }

    private _showNotification(notification: IAlert) {
        this._snack.show(notification.data.message, {
            type: SEVERITY_SNACKBAR_MAP.get(notification.severity),
        });
    }
}
